import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { auth } from "./Components/Config/firebase"; 
import LandingPage from "./Components/Common/LandingPage";
import Sidebar from "./Components/Common/Navbar";
import SubmitRequest from "./Components/SubmitRequest/SubmitRequest";
import SubmittedRequests from "./Components/SubmitRequest/SubmittedRequests";
import Home from "./Components/Common/Home";
import HeicToPngConverter from "./Components/heicconvertor";
import ExplorerAiChat from "./Components/Common/ExplorerAiChat";
import SupportTicketsCenter from "./Components/Common/SupportTicketsCenter";

import Location from "./Components/Common/Location";

import Discount from "./Components/Common/Discount";
import InfoList from "./Components/Info/InfoList";
import InfoCreate from "./Components/Info/InfoCreate";
import InfoView from "./Components/Info/InfoView";
import InfoEdit from "./Components/Info/InfoEdit";
import InfoSectionCreate from "./Components/Info/InfoSectionCreate";
import InfoSectionEdit from "./Components/Info/InfoSectionEdit";
import UserView from "./Components/Info/UserView";
import Ebay from "./Components/Ebay/Ebay";

import WhatsNewEmailEditor from "./Components/Sendgrid/WhatsNewEmailEditor";
import ReleaseNotesEmailGallery from "./Components/Sendgrid/ReleaseNotesEmailGallery";
import MarketingEmail from "./Components/Sendgrid/MarketingEmail";

import AnnouncementsEmailEditor from "./Components/Sendgrid/AnnouncementsEmailEditor";
import UnsubscribePage from "./Components/Sendgrid/UnsubscribePage";
import MarketingContacts from "./Components/Sendgrid/MarketingContacts";
import MarketingCampaignsAdmin from "./Components/Sendgrid/MarketingCampaignAdmin";
import HibidDashboard from "./Components/Hibid/HibidDashboard";
import ShippingAgreementPage from "./Components/Hibid/ShippingAgreementPage";
import DeliverySchedulingPage from "./Components/Hibid/DeliverySchedulingPage";
import PickupSchedulingPage from "./Components/Hibid/PickupSchedulingPage";



const Admin = lazy(() => import("./Components/Common/Admin"));
const AuctionFuelFeedbacks = lazy(() => import("./Components/Common/AuctionFuelFeedbacks"));
const CreatePods = lazy(() => import("./Components/AI/CreatePods"));
const ManualLotCreator = lazy(() => import("./Components/AI/IndividualLotCreator/ManualLotCreator"));
const BulkLotCreator = lazy(() => import("./Components/AI/BulkLotCreator"));
const Results = lazy(() => import("./Components/AI/Results/Results"));
const AuctionFuelPortal = lazy(() => import("./Components/AuctionFuel/AuctionFuelPortal"));

const UserSettings = lazy(() => import("./Components/Common/UserSettings"));
const ClearCacheCheatSheet = lazy(() => import("./Components/Help/ClearCacheCheatSheet"));

const LoadingIndicator = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100dvh",
        paddingBottom: "env(safe-area-inset-bottom)",
        fontSize: "18px",
        color: "#555",
      }}
    >
      Loading...
    </div>
  );
};

const SupportTicketLegacyRedirect = () => {
  const { ticketId } = useParams();
  const encodedTicketId = encodeURIComponent(String(ticketId || "").trim());
  return <Navigate to={`/support-tickets?ticketId=${encodedTicketId}`} replace />;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      setUser(authUser);
      setLoading(false); // Stop loading once the auth state is determined
    });
    return unsubscribe; // Cleanup the subscription
  }, []);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Router>
      <Routes>
        {/* Public Route */}
        <Route
          path="/"
          element={user ? <Dashboard /> : <LandingPage />}
        />
        <Route
          path="unsubscribe"
          element={
            <Suspense fallback={<LoadingIndicator />}>
              <UnsubscribePage />
            </Suspense>
          }
        />
        <Route
          path="shipping-agreement"
          element={
            <Suspense fallback={<LoadingIndicator />}>
              <ShippingAgreementPage />
            </Suspense>
          }
        />
        <Route
          path="delivery-scheduling"
          element={
            <Suspense fallback={<LoadingIndicator />}>
              <DeliverySchedulingPage />
            </Suspense>
          }
        />
        <Route
          path="pickup-scheduling"
          element={
            <Suspense fallback={<LoadingIndicator />}>
              <PickupSchedulingPage />
            </Suspense>
          }
        />
        <Route
          path="/submitrequest"
          element={<SubmitRequest />}
        />
        <Route path="/support-tickets/:ticketId" element={<SupportTicketLegacyRedirect />} />
        <Route path="documentation" element={<UserView />} />
        <Route path="docs" element={<InfoList />} />
        <Route path="docs/create" element={<InfoCreate />} />
        <Route path="docs/:docId" element={<InfoView />} />
        <Route path="docs/:docId/edit" element={<InfoEdit />} />
        <Route path="docs/:docId/sections/create" element={<InfoSectionCreate />} />
        <Route path="docs/:docId/sections/:sectionId/edit" element={<InfoSectionEdit />} />
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            user ? (
              <Suspense fallback={<LoadingIndicator />}>
                <Dashboard />
              </Suspense>
            ) : (
              <Navigate to="/" />
            )
          }
        />
      </Routes>
      {user ? <ExplorerAiChat /> : null}
    </Router>
  );
};

const Dashboard = () => {
  return (
    <div
      className="font-gill overflow-auto bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50"
      style={{ minHeight: "100dvh", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* <Banner /> */}
      <div style={{ display: "flex" }}>
        <Sidebar />
        <div style={{ flex: 1 }} className="px-2">
          <Routes>

            <Route
              path=""
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Home />
                </Suspense>
              }
            />
            <Route
              path="createpods"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <CreatePods />
                </Suspense>
              }
            />
            <Route
              path="ebay"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Ebay />
                </Suspense>
              }
            />
            <Route
              path="location"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Location />
                </Suspense>
              }
            />
            <Route
              path="heic"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <HeicToPngConverter />
                </Suspense>
              }
            />


            <Route
              path="usersettings"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <UserSettings />
                </Suspense>
              }
            />
            <Route
              path="manual"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <ManualLotCreator />
                </Suspense>
              }
            />
            <Route
              path="discount"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Discount />
                </Suspense>
              }
            />
            <Route
              path="releasenotesemail"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <WhatsNewEmailEditor />
                </Suspense>
              }
            />
            <Route
              path="announcementemail"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <AnnouncementsEmailEditor />
                </Suspense>
              }
            />


            <Route
              path="marketingemail"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <MarketingEmail />
                </Suspense>
              }
            />

            <Route
              path="email"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <ReleaseNotesEmailGallery />
                </Suspense>
              }
            />
            <Route
              path="contacts"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <MarketingContacts />
                </Suspense>
              }
            />
            <Route
              path="hibid/*"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <HibidDashboard />
                </Suspense>
              }
            />
            <Route
              path="auctionfuel/*"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <AuctionFuelPortal />
                </Suspense>
              }
            />
            
            <Route
              path="admin"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Admin />
                </Suspense>
              }
            />
            <Route
              path="admin/auctionfuel-feedbacks"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <AuctionFuelFeedbacks />
                </Suspense>
              }
            />
            <Route
              path="admin/support-tickets"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Navigate to="/support-tickets" replace />
                </Suspense>
              }
            />
            <Route
              path="support-tickets"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <SupportTicketsCenter />
                </Suspense>
              }
            />
                <Route
              path="marketingadmin"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <MarketingCampaignsAdmin />
                </Suspense>
              }
            />
            <Route
              path="bulk"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <BulkLotCreator />
                </Suspense>
              }
            />
            <Route
              path="submittedrequests"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <SubmittedRequests />
                </Suspense>
              }
            />
            <Route
              path="results"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <Results />
                </Suspense>
              }
            />
            <Route
              path="clear-cache-guide"
              element={
                <Suspense fallback={<LoadingIndicator />}>
                  <ClearCacheCheatSheet />
                </Suspense>
              }
            />

          </Routes>
        </div>
      </div>
    </div>
  );
};

export default App;
